// Pinch-to-zoom and pan for big grids (16×16, samurai), by changing the grid's SVG viewBox so
// the drawing stays sharp and pointer-to-cell mapping keeps working through getScreenCTM.
// Two fingers pinch and pan; one finger still selects cells (the grid gets those events). Ctrl
// or Cmd + wheel (and a trackpad pinch) zooms at the pointer; a plain wheel pans once zoomed.
import type { Rc } from '@zinc/shared';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from 'react';
import {
  centreOn,
  ensureVisible,
  panBy,
  pinch,
  scaleOf,
  zoomAt,
  type Fraction,
  type Rect,
} from '../../lib/zoom';
import { CELL } from './constraints/geom';
import { FitIcon } from './icons';

export interface ZoomHandle {
  /** Pans just enough to show the cell (keyboard moves). */
  reveal: (cell: Rc) => void;
}

interface ZoomViewProps {
  /** The whole drawing in SVG units; fixed for the component's life. */
  frame: Rect;
  /** Off: the grid shows whole and every pointer goes to it, as before. */
  enabled: boolean;
  maxScale: number;
  /** Two fingers went down: the grid should drop the one-finger selection it started. */
  onGestureStart?: () => void;
  /** Overview map for the zoomed state, given the view and a jump function. */
  overview?: (view: Rect, jump: (x: number, y: number) => void) => ReactNode;
  children: (view: Rect | undefined) => ReactNode;
  ref?: Ref<ZoomHandle>;
}

type Point = { x: number; y: number };
type Jump = (x: number, y: number) => void;

function OverviewSlot(props: {
  render: (view: Rect, jump: Jump) => ReactNode;
  view: Rect;
  jump: Jump;
}) {
  return props.render(props.view, props.jump);
}

const ZOOMED = 1.001;

export function ZoomView({
  frame,
  enabled,
  maxScale,
  onGestureStart,
  overview,
  children,
  ref,
}: ZoomViewProps) {
  const surface = useRef<HTMLDivElement>(null);
  const [view, setViewState] = useState<Rect>(frame);
  const viewRef = useRef<Rect>(frame);
  const setView = useCallback((v: Rect) => {
    viewRef.current = v;
    setViewState(v);
  }, []);

  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<{ start: Rect; from: Fraction; spread: number } | null>(null);
  const gesturing = useRef(false);

  const fraction = (x: number, y: number): Fraction => {
    const r = surface.current?.getBoundingClientRect();
    if (!r || r.width === 0 || r.height === 0) return { fx: 0.5, fy: 0.5 };
    return { fx: (x - r.left) / r.width, fy: (y - r.top) / r.height };
  };
  const pair = (): [Point, Point] | null => {
    const [a, b] = [...pointers.current.values()];
    return a && b ? [a, b] : null;
  };
  const begin = () => {
    const p = pair();
    if (!p) return;
    const [a, b] = p;
    gesture.current = {
      start: viewRef.current,
      from: fraction((a.x + b.x) / 2, (a.y + b.y) / 2),
      spread: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
    };
  };

  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!enabled || e.pointerType === 'mouse') return;
    // A primary pointer starts a fresh touch, so anything left from a lost pointerup goes.
    if (e.isPrimary) {
      pointers.current.clear();
      gesture.current = null;
      gesturing.current = false;
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size < 2) return;
    // The second finger never reaches the grid; the first one's selection is undone.
    e.stopPropagation();
    surface.current?.setPointerCapture?.(e.pointerId);
    if (!gesturing.current) {
      gesturing.current = true;
      onGestureStart?.();
    }
    begin();
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const p = pointers.current.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX;
    p.y = e.clientY;
    if (!gesturing.current) return;
    e.stopPropagation();
    const g = gesture.current;
    const two = pair();
    if (!g || !two) return;
    const [a, b] = two;
    const spread = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)) / g.spread;
    const to = fraction((a.x + b.x) / 2, (a.y + b.y) / 2);
    setView(pinch(frame, g.start, g.from, to, spread, maxScale));
  };
  const onEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.delete(e.pointerId)) return;
    if (!gesturing.current) return;
    e.stopPropagation();
    if (pointers.current.size >= 2) begin();
    else gesture.current = null;
    // Until every finger is up, the one left behind doesn't select.
    if (pointers.current.size === 0) gesturing.current = false;
  };

  // Wheel and Safari's trackpad gestures need non-passive listeners to stop the page zooming.
  useEffect(() => {
    const el = surface.current;
    if (!el || !enabled) return;
    const rect = () => el.getBoundingClientRect();
    const at = (x: number, y: number): Fraction => {
      const r = rect();
      return r.width && r.height
        ? { fx: (x - r.left) / r.width, fy: (y - r.top) / r.height }
        : { fx: 0.5, fy: 0.5 };
    };
    const onWheel = (e: WheelEvent) => {
      const v = viewRef.current;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const dy = Math.max(-50, Math.min(50, e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY));
        setView(zoomAt(frame, v, Math.exp(-dy * 0.01), at(e.clientX, e.clientY), maxScale));
      } else if (scaleOf(frame, v) > ZOOMED) {
        e.preventDefault();
        const r = rect();
        if (!r.width || !r.height) return;
        const k = e.deltaMode === 1 ? 16 : 1;
        setView(panBy(frame, v, (e.deltaX * k * v.w) / r.width, (e.deltaY * k * v.h) / r.height));
      }
    };
    // Safari (macOS trackpad) sends gesture events instead of ctrl+wheel. On iOS the same
    // events come with touch pointers, which the pinch above already handles.
    type GestureEvent = UIEvent & { scale: number; clientX: number; clientY: number };
    let start: Rect | null = null;
    const onGestureStartEv = (e: Event) => {
      e.preventDefault();
      start = pointers.current.size === 0 ? viewRef.current : null;
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const ge = e as GestureEvent;
      if (!start || pointers.current.size > 0) return;
      setView(zoomAt(frame, start, ge.scale, at(ge.clientX, ge.clientY), maxScale));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', onGestureStartEv);
    el.addEventListener('gesturechange', onGestureChange);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', onGestureStartEv);
      el.removeEventListener('gesturechange', onGestureChange);
    };
  }, [enabled, frame, maxScale, setView]);

  const controls = useRef<HTMLDivElement>(null);
  useImperativeHandle(
    ref,
    () => ({
      reveal: ([r, c]: Rc) => {
        if (!enabled) return;
        const v = viewRef.current;
        if (scaleOf(frame, v) <= ZOOMED) return;
        const cell = { x: c * CELL, y: r * CELL, w: CELL, h: CELL };
        const margin = CELL / 4;
        let next = ensureVisible(frame, v, cell, margin);
        // The controls cover the view's top-right corner: move the cell out from under them.
        const sr = surface.current?.getBoundingClientRect();
        const or = controls.current?.getBoundingClientRect();
        if (sr && or && sr.width > 0 && sr.height > 0) {
          const left = (n: Rect) => n.x + ((or.left - sr.left) / sr.width) * n.w;
          const bottom = (n: Rect) => n.y + ((or.bottom - sr.top) / sr.height) * n.h;
          const under = (n: Rect) => cell.x + cell.w > left(n) && cell.y < bottom(n);
          if (under(next)) next = panBy(frame, next, cell.x + cell.w + margin - left(next), 0);
          if (under(next)) next = panBy(frame, next, 0, cell.y - margin - bottom(next));
        }
        if (next !== v) setView(next);
      },
    }),
    [enabled, frame, setView],
  );

  const jump = (x: number, y: number) => setView(centreOn(frame, scaleOf(frame, view), x, y));

  const zoomed = enabled && scaleOf(frame, view) > ZOOMED;
  return (
    <div className="relative">
      <div
        ref={surface}
        data-zoom={enabled ? scaleOf(frame, view).toFixed(2) : undefined}
        style={enabled ? { touchAction: 'none' } : undefined}
        onPointerDownCapture={onDown}
        onPointerMoveCapture={onMove}
        onPointerUpCapture={onEnd}
        onPointerCancelCapture={onEnd}
      >
        {children(enabled ? view : undefined)}
      </div>
      {zoomed && (
        <div ref={controls} className="absolute top-2 right-2 flex flex-col items-end gap-2">
          <button
            type="button"
            aria-label="Reset zoom"
            onClick={() => setView(frame)}
            className="flex h-11 w-11 items-center justify-center rounded-btn border border-line bg-surface p-0 text-ink shadow-sm"
          >
            <FitIcon />
          </button>
          {overview && <OverviewSlot render={overview} view={view} jump={jump} />}
        </div>
      )}
    </div>
  );
}
